import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { normalizeColumnsLayout } from 'src/helpers/columnsLayoutUtils';
import {
  addPageBlock,
  moveBlockOrder,
  removePageBlock,
  updatePageBlock,
} from 'src/helpers/pageDocumentUtils';
import type { IPageDocument } from 'src/helpers/pageDocumentUtils';
import { assignBlockSortOrders } from 'src/helpers/pageContentUtils';
import { documentHasSignupFlowBlock } from 'src/helpers/signupPageLayoutUtils';
import { LanguageKeys } from 'src/common/constants/languages';
import { IColumnsBlock, IPageBlock, ITrackMapBlock, PageBlockType } from 'src/interfaces/PageBlock';
import { IPageColumn } from 'src/interfaces/PageColumn';
import { ITrackMap } from 'src/interfaces/TrackMap';
import ColumnDropZone from 'src/pages/Dashboard/System/Pages/components/ColumnDropZone';
import SortableColumnShell from 'src/pages/Dashboard/System/Pages/components/SortableColumnShell';

interface ColumnsLayoutEditorProps {
  layout: IColumnsBlock;
  document: IPageDocument;
  onDocumentChange: (doc: IPageDocument) => void;
  onColumnsPatch: (patch: Partial<IColumnsBlock>) => void;
  selectedEditorLang: LanguageKeys;
  setSelectedEditorLang: (lang: LanguageKeys) => void;
  contentKey: string;
  pageContentId?: string;
  trackMaps: ITrackMap[];
  showStackOrderControls?: boolean;
  hasSponsorsBlockInPage?: boolean;
  isSignupShell?: boolean;
}

export default function ColumnsLayoutEditor({
  layout: rawLayout,
  document,
  onDocumentChange,
  onColumnsPatch,
  selectedEditorLang,
  setSelectedEditorLang,
  contentKey,
  pageContentId,
  trackMaps,
  showStackOrderControls = true,
  hasSponsorsBlockInPage = false,
  isSignupShell = false,
}: ColumnsLayoutEditorProps) {
  const { t } = useTranslation();

  const layout = useMemo(() => {
    const normalized = normalizeColumnsLayout(rawLayout);
    if (!normalized) return rawLayout;
    return { ...rawLayout, ...normalized };
  }, [rawLayout]);

  const n = layout.columns.length;

  const handleLayoutPatch = (patch: Partial<IColumnsBlock>) => {
    onColumnsPatch(patch);
  };

  const handleColumnChange = (columnIndex: number, patch: Partial<IPageColumn>) => {
    const columns = [...layout.columns];
    columns[columnIndex] = {
      ...columns[columnIndex]!,
      ...patch,
      ...(patch.blocks ? { blocks: assignBlockSortOrders(patch.blocks) } : {}),
    };
    handleLayoutPatch({ columns });
  };

  const handleAddBlock = (
    columnIndex: number,
    type: PageBlockType,
    insertIndex?: number,
  ) => {
    if (type === 'sponsors' && hasSponsorsBlockInPage) return;
    if (type === 'signupFlow' && isSignupShell && documentHasSignupFlowBlock(document)) return;
    if (type === 'columns') return;
    let options: { trackMapId?: string } | undefined;
    if (type === 'trackMap') {
      const usedTrackMapIds = new Set(
        layout.columns
          .flatMap(c => c.blocks)
          .filter((b): b is ITrackMapBlock => b.type === 'trackMap')
          .map(b => String(b.trackMapId)),
      );
      const trackMapIds = trackMaps.map(m => String(m.id));
      options = {
        trackMapId: trackMapIds.find(id => !usedTrackMapIds.has(id)) ?? trackMapIds[0] ?? '',
      };
    }
    onDocumentChange(
      addPageBlock(
        document,
        {
          kind: 'column',
          columnsBlockId: layout.id,
          columnIndex,
          insertIndex,
        },
        type,
        options,
      ),
    );
  };

  const sharedColumnProps = {
    layout,
    document,
    contentKey,
    pageContentId,
    selectedEditorLang,
    setSelectedEditorLang,
    trackMaps,
    onColumnChange: handleColumnChange,
    onAddBlock: handleAddBlock,
    onUpdate: (id: string, patch: Partial<IPageBlock>) =>
      onDocumentChange(updatePageBlock(document, id, patch)),
    onRemove: (id: string) => onDocumentChange(removePageBlock(document, id)),
    onMove: (id: string, direction: 'up' | 'down') =>
      onDocumentChange(moveBlockOrder(document, id, direction)),
    onColumnsChange: (_id: string, patch: Partial<IPageBlock>) =>
      onColumnsPatch(patch as Partial<IColumnsBlock>),
    onDocumentChange,
    isSignupShell,
  };

  return (
    <Box>
      {showStackOrderControls && n > 2 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('system.pages.mobileColumnOrderHint')}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 2,
        }}
      >
        {layout.columns.map((_, columnIndex) => (
          <SortableColumnShell
            key={`${layout.id}-col-shell-${columnIndex}`}
            columnsBlockId={layout.id}
            columnIndex={columnIndex}
            showColumnDrag={n >= 2}
          >
            {({ attributes, listeners }) => (
              <ColumnDropZone
                columnsBlockId={layout.id}
                columnIndex={columnIndex}
                {...sharedColumnProps}
                showColumnDrag={n >= 2}
                columnSortableAttributes={attributes}
                columnSortableListeners={listeners}
              />
            )}
          </SortableColumnShell>
        ))}
      </Box>
    </Box>
  );
}
